/**
 * Finalization Step
 * Step 3: Show processing indicator while summary is generated
 */

import React, { useEffect } from 'react';
import { Box, CircularProgress, Typography, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { ReportStatus } from '../../types/insurance';
import type { PDFUploadState } from '../../hooks/useInsuranceReport';

interface FinalizationStepProps {
  reportStatus: ReportStatus | null;
  uploadedPDFs: PDFUploadState[];
  pollStatus: () => Promise<void>;
}

const FinalizationStep: React.FC<FinalizationStepProps> = ({
  reportStatus,
  uploadedPDFs,
  pollStatus,
}) => {
  useEffect(() => {
    // Start polling when component mounts
    const pollInterval = setInterval(() => {
      pollStatus();
    }, 2000); // Poll every 2 seconds

    return () => {
      clearInterval(pollInterval);
    };
  }, [pollStatus]);

  const completedPDFs = uploadedPDFs.filter((p) => p.status === 'completed').length;

  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CircularProgress size={80} sx={{ mb: 3 }} />

      <Typography variant="h5" gutterBottom>
        Generating Insurance Summary...
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Analyzing medical records and consolidating findings
      </Typography>

      <Chip
        icon={<CheckCircleIcon />}
        label={`${completedPDFs} / ${uploadedPDFs.length} PDFs processed`}
        color="primary"
        sx={{ mb: 2 }}
      />

      <Typography variant="body2" color="text.secondary">
        Status: {reportStatus || 'Processing...'}
      </Typography>
    </Box>
  );
};

export default FinalizationStep;
